import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { LiveChat } from './components/LiveChat';
import { StickyMobileCTA } from './components/StickyMobileCTA';
import { ScrollToTopButton } from './components/ScrollToTopButton';
import { PageTransition } from './components/PageTransition';
// Home is the landing route — keep it eager so the first paint is instant.
import { Home } from './pages/Home';
import { CartProvider } from './components/CartContext';
import { CartDrawer } from './components/CartDrawer';
import { AnnouncementBar } from './components/AnnouncementBar';
import { I18nProvider } from './utils/i18n';
import { trackMetaPageView } from './utils/metaPixel';
// Every other route is code-split and fetched on demand, so they no longer
// weigh down the initial bundle. Named exports are mapped to a default for lazy.
const Contact = lazy(() => import('./pages/Contact').then(m => ({
  default: m.Contact
})));
const Product = lazy(() => import('./pages/Product').then(m => ({
  default: m.Product
})));
const Project = lazy(() => import('./pages/Project').then(m => ({
  default: m.Project
})));
const HelpMeChoose = lazy(() => import('./pages/HelpMeChoose').then(m => ({
  default: m.HelpMeChoose
})));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({
  default: m.AboutPage
})));
const DeliveryZones = lazy(() => import('./pages/DeliveryZones').then(m => ({
  default: m.DeliveryZones
})));
const Blog = lazy(() => import('./pages/Blog').then(m => ({
  default: m.Blog
})));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({
  default: m.BlogPost
})));
const DeliveryPrep = lazy(() => import('./pages/DeliveryPrep').then(m => ({
  default: m.DeliveryPrep
})));
const CompareMaterials = lazy(() => import('./pages/CompareMaterials').then(m => ({
  default: m.CompareMaterials
})));
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({
  default: m.NotFound
})));
const TrackOrder = lazy(() => import('./pages/TrackOrder').then(m => ({
  default: m.TrackOrder
})));
const TrackOrderLookup = lazy(() => import('./pages/TrackOrderLookup').then(m => ({
  default: m.TrackOrderLookup
})));
const Calculator = lazy(() => import('./pages/Calculator').then(m => ({
  default: m.Calculator
})));
const CityMaterial = lazy(() => import('./pages/CityMaterial').then(m => ({
  default: m.CityMaterial
})));
const County = lazy(() => import('./pages/County').then(m => ({
  default: m.County
})));
const ServiceAreas = lazy(() => import('./pages/ServiceAreas').then(m => ({
  default: m.ServiceAreas
})));
const LogoConcepts = lazy(() => import('./components/LogoConcepts').then(m => ({
  default: m.LogoConcepts
})));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy').then(m => ({
  default: m.RefundPolicy
})));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(m => ({
  default: m.PrivacyPolicy
})));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(m => ({
  default: m.TermsOfService
})));
const DailyTicketForm = lazy(() => import('./pages/DailyTicketForm').then(m => ({
  default: m.DailyTicketForm
})));
function RouteFallback() {
  return <div className="min-h-[60vh] flex items-center justify-center bg-[#fdfbf7] dark:bg-[#222222]" role="status" aria-label="Loading" data-id="element-0" data-mp="1r3r6id9uykel">
      <span className="h-8 w-8 rounded-full border-2 border-brand-orange border-t-transparent animate-spin" data-id="element-1" data-mp="1w2tyaw0tuu4m" />
    </div>;
}
// Client-side routing never reloads the document, so the pixel's initial
// PageView is the only one it would ever see. This reports each subsequent
// route as its own page view.
function MetaPixelPageViews() {
  const location = useLocation();
  useEffect(() => {
    trackMetaPageView();
  }, [location.pathname]);
  return null;
}
function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Two-pass scroll: first attempt accounts for the sticky navbar height,
      // then a follow-up corrects for late-rendering content (Services, etc.)
      // that can push the target further down the page.
      const scrollToTarget = () => {
        const el = document.getElementById(id);
        if (!el) return;
        const navHeight = document.querySelector('nav')?.getBoundingClientRect().height || 80;
        const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 12;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'smooth'
        });
      };
      const t1 = setTimeout(scrollToTarget, 120);
      const t2 = setTimeout(scrollToTarget, 600);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return null;
}
function AnimatedRoutes() {
  const location = useLocation();
  return <AnimatePresence mode="wait" data-id="element-2" data-mp="205igt3c24vl4">
      <Suspense fallback={<RouteFallback data-id="element-4" data-mp="206a5rvb5fv2f" />} data-id="element-3" data-mp="206timxlxzt9l">
        <Routes location={location} key={location.pathname} data-id="element-5" data-mp="2553ihlim3i1j">
          <Route path="/" element={<PageTransition data-id="element-7" data-mp="253a6rrt567mo">
                <Home data-id="element-8" data-mp="29wd1w4dkhp4z" />
              </PageTransition>} data-id="element-6" data-mp="253sdxjhce69d" />
          <Route path="/contact" element={<PageTransition data-id="element-10" data-mp="2ev6i44k8nu8u">
                <Contact data-id="element-11" data-mp="2etv7bksdlm88" />
              </PageTransition>} data-id="element-9" data-mp="29xwkw2qlyeag" />
          <Route path="/shop/:slug" element={<PageTransition data-id="element-13" data-mp="2jsyaezospidk">
                <Product data-id="element-14" data-mp="2js6flr30vooc" />
              </PageTransition>} data-id="element-12" data-mp="2jtzlv59q46vt" />
          <Route path="/projects/:slug" element={<PageTransition data-id="element-16" data-mp="2nwe53qhb18nm">
                <Project data-id="element-17" data-mp="2supdrxvv5d49" />
              </PageTransition>} data-id="element-15" data-mp="2nvcsviojyg4b" />
          <Route path="/help" element={<PageTransition data-id="element-19" data-mp="2ssbmm04b0l1z">
                <HelpMeChoose data-id="element-20" data-mp="ti3epdl2a6fq" />
              </PageTransition>} data-id="element-18" data-mp="2su5x0khy8urt" />
          <Route path="/about" element={<PageTransition data-id="element-22" data-mp="ygeo5n7g2exf">
                <AboutPage data-id="element-23" data-mp="yhg1xzfuhfiu" />
              </PageTransition>} data-id="element-21" data-mp="tjodxgibmdjx" />
          <Route path="/delivery-zones" element={<PageTransition data-id="element-25" data-mp="21slus3sq7aw4">
                <DeliveryZones data-id="element-26" data-mp="21toik2ur4x7t" />
              </PageTransition>} data-id="element-24" data-mp="21raonxbt5r1u" />
          <Route path="/subcontractors" element={<Navigate to="/" replace data-id="element-28" data-mp="1wukbpivavbkn" />} data-id="element-27" data-mp="1wtix54f2s6y6" />
          <Route path="/contractor-ordering" element={<Navigate to="/" replace data-id="element-30" data-mp="2atjysm35ziep" />} data-id="element-29" data-mp="2ass3lcdhbmoy" />
          <Route path="/diy-help" element={<Navigate to="/blog/perfect-gravel-driveway" replace data-id="element-32" data-mp="25s4a8k1s2trk" />} data-id="element-31" data-mp="25tfgcqip4dlu" />
          <Route path="/blog" element={<PageTransition data-id="element-34" data-mp="2kpemhvm8w3oc">
                <Blog data-id="element-35" data-mp="2kolk16ubk0c0" />
              </PageTransition>} data-id="element-33" data-mp="2kq6iv8no25fo" />
          <Route path="/blog/:slug" element={<PageTransition data-id="element-37" data-mp="2fqttag5euk9e">
                <BlogPost data-id="element-38" data-mp="2tiqkmhz98q4q" />
              </PageTransition>} data-id="element-36" data-mp="2fs506ou5k84q" />
          <Route path="/delivery-prep" element={<PageTransition data-id="element-40" data-mp="2tkky5b875fys">
                <DeliveryPrep data-id="element-41" data-mp="2pgwmagcj7y7q" />
              </PageTransition>} data-id="element-39" data-mp="2ti8fswyj13l7" />
          <Route path="/compare" element={<PageTransition data-id="element-43" data-mp="33gehbdgf87pj">
                <CompareMaterials data-id="element-44" data-mp="33hph726qwvbd" />
              </PageTransition>} data-id="element-42" data-mp="2pjiv0jb5sjrj" />
          <Route path="/calculator" element={<PageTransition data-id="element-46" data-mp="2yjeah0bqz0xd">
                <Calculator data-id="element-47" data-mp="2yg083s332rpx" />
              </PageTransition>} data-id="element-45" data-mp="2yicvwlvivwaw" />
          {/* "Why shop local" is now the second half of the About page, so the
              old standalone URL redirects there rather than 404ing. */}
          <Route path="/local-vs-national" element={<Navigate to="/about" replace data-id="element-49" data-mp="384nlt18iyrh5" />} data-id="element-48" data-mp="385gtc4fb5l0c" />
          <Route path="/track" element={<PageTransition data-id="element-51" data-mp="3d40h8adgp6vc">
                <TrackOrderLookup data-id="element-52" data-mp="3d2yvnbytl1zd" />
              </PageTransition>} data-id="element-50" data-mp="387t8yaxlfja8" />
          <Route path="/track/:orderId" element={<PageTransition data-id="element-54" data-mp="2zbkelaoh3auy">
                <TrackOrder data-id="element-55" data-mp="2zbanfjroyxno" />
              </PageTransition>} data-id="element-53" data-mp="2zclnp9lxhna1" />
          <Route path="/service-areas" element={<PageTransition data-id="element-57" data-mp="345oh9hqpgwq8">
                <ServiceAreas data-id="element-58" data-mp="2pfqqcu1e5sxc" />
              </PageTransition>} data-id="element-56" data-mp="345estylb6xmn" />
          <Route path="/logo-concepts" element={<PageTransition data-id="element-60" data-mp="2ud936vij4sam">
                <LogoConcepts data-id="element-61" data-mp="2uek3umgohjxi" />
              </PageTransition>} data-id="element-59" data-mp="2pd4lj25vxrit" />
          <Route path="/refund-policy" element={<PageTransition data-id="element-63" data-mp="2gesw33gr8zry">
                <RefundPolicy data-id="element-64" data-mp="2ggn9lwpp5pm0" />
              </PageTransition>} data-id="element-62" data-mp="2gdrbo8dun0xk" />
          <Route path="/privacy-policy" element={<PageTransition data-id="element-66" data-mp="30vdjo8fiwx42">
                <PrivacyPolicy data-id="element-67" data-mp="30vnd65zs1mei" />
              </PageTransition>} data-id="element-65" data-mp="2kfcu0995k7f5" />
          <Route path="/terms-of-service" element={<PageTransition data-id="element-69" data-mp="2vw0uvi53dfyu">
                <TermsOfService data-id="element-70" data-mp="2vx29fwlbgklb" />
              </PageTransition>} data-id="element-68" data-mp="30t11pv9rmmr0" />
          <Route path="/form" element={<PageTransition data-id="element-72" data-mp="2qvmp66qyqi3z">
                <DailyTicketForm data-id="element-73" data-mp="2qz0p78c5mf89" />
              </PageTransition>} data-id="element-71" data-mp="2qwekrhkk8fu9" />

          {/* County-level landing pages. Distinct from the two-segment
              city × material route below — React Router matches on segment
              count, so the two coexist. County.tsx renders the 404 page for
              any slug outside SERVICE_COUNTIES. */}
          <Route path="/gravel-delivery/:countySlug" element={<PageTransition data-id="element-75" data-mp="284dbrme2n974">
                <County data-id="element-76" data-mp="23851oneqe66t" />
              </PageTransition>} data-id="element-74" data-mp="287rg306apsh7" />
          <Route path="/gravel-delivery/:citySlug/:materialSlug" element={<PageTransition data-id="element-78" data-mp="1y78p7lgbdbps">
                <CityMaterial data-id="element-79" data-mp="1yamrktoz9kx8" />
              </PageTransition>} data-id="element-77" data-mp="235ini4xc4sfm" />
          <Route path="*" element={<PageTransition data-id="element-81" data-mp="1u7qgrp5oit3u">
                <NotFound data-id="element-82" data-mp="9owe4bevy6m0" />
              </PageTransition>} data-id="element-80" data-mp="1u7zx0l0x9lwc" />
        </Routes>
      </Suspense>
    </AnimatePresence>;
}
function AppContent({
  isDark,
  toggleDark
}: {
  isDark: boolean;
  toggleDark: () => void;
}) {
  const location = useLocation();
  const isContractorPage = location.pathname.includes('subcontractors') || location.pathname.includes('contractor');
  // The daily ticket form (/form) is a standalone operational document. It
  // renders bare — no site chrome (navbar, footer, announcement bar, live
  // chat, sticky CTA, cart) — so it prints clean and stays out of the way.
  const isStandalonePage = location.pathname === '/form';
  if (isStandalonePage) {
    // The daily ticket is a printed white document — never apply the `dark`
    // class here so it always renders on a clean white surface regardless of
    // the visitor's theme.
    return <div className="min-h-screen font-sans" data-id="element-83" data-mp="33fjmudwbcjjk">
        <ScrollToHash data-id="element-84" data-mp="38ed2abv5ukmd" />
        <MetaPixelPageViews data-id="element-85" data-mp="38duy8t29b23w" />
        <AnimatedRoutes data-id="element-86" data-mp="38d218jt3nqyy" />
      </div>;
  }
  return <div className={`min-h-screen flex flex-col font-sans ${isDark ? 'dark' : ''} ${isContractorPage ? 'contractor-theme' : ''}`} data-id="element-87" data-mp="2f7n0fhb65reo">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-brand-orange focus:text-white focus:rounded-lg focus:font-bold focus:outline-none focus:ring-2 focus:ring-white" data-id="element-88" data-mp="2k4wy1k8pp9dl">
        Skip to main content
      </a>
      <div className="flex-grow bg-[#fdfbf7] dark:bg-[#222222] transition-colors duration-300 pb-16 md:pb-0" id="main-content" data-id="element-89" data-mp="1wi6yqqnqzswo">
        <ScrollToHash data-id="element-90" data-mp="21ddwlhuc63do" />
        <MetaPixelPageViews data-id="element-91" data-mp="26f39l314cr4e" />
        <AnnouncementBar data-id="element-92" data-mp="26ds48ys0zbb6" />
        <Navbar isDark={isDark} toggleDark={toggleDark} data-id="element-93" data-mp="26diglhugdg8n" />
        <AnimatedRoutes data-id="element-94" data-mp="2b5s0ag4z5k2h" />
        <Footer data-id="element-95" data-mp="2b735mke2izvp" />
        <LiveChat data-id="element-96" data-mp="2b7mnk13pxo9q" />
        <StickyMobileCTA data-id="element-97" data-mp="1dq5t95ggx4ea" />
        <ScrollToTopButton data-id="element-98" data-mp="1dqxp8hdz9453" />
        <CartDrawer data-id="element-99" data-mp="1dpmluiog1yei" />
      </div>
    </div>;
}
// Decide the initial theme for a first-time visitor (no saved preference yet):
//   1. An explicit saved choice always wins — a returning visitor who picked a
//      theme keeps it.
//   2. Everyone else starts in LIGHT mode.
//
// Light is the deliberate default. Customers are contractors and homeowners
// pricing material outdoors on a phone in daylight, so light mode is the
// higher-value surface and the one held to the tightest design standard. The
// previous behavior deferred to the OS `prefers-color-scheme` and then to a
// time-of-day clock rule, which meant a majority of first-time visitors landed
// in dark mode by accident — and made the very first impression the mode that
// gets less design attention. The toggle still switches modes and still
// persists, so nothing is taken away from anyone who prefers dark.
function getInitialTheme(): boolean {
  try {
    const saved = localStorage.getItem('theme');
    if (saved) {
      return saved === 'dark';
    }
  } catch {
    // localStorage can throw in private mode / embedded webviews / when
    // storage is blocked. Fall through to the light default.
  }
  return false;
}
export function App() {
  const [isDark, setIsDark] = useState(getInitialTheme);
  useEffect(() => {
    try {
      localStorage.setItem('theme', isDark ? 'dark' : 'light');
    } catch {}
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);
  const toggleDark = () => setIsDark(!isDark);
  return <I18nProvider data-id="element-100" data-mp="2yeh9cbqkk8ld">
      <Router data-id="element-101" data-mp="2yg0nnwso09ki">
        <CartProvider data-id="element-102" data-mp="33d0vac55xkdq">
          <AppContent isDark={isDark} toggleDark={toggleDark} data-id="element-103" data-mp="33dacba88ch7a" />
        </CartProvider>
      </Router>
    </I18nProvider>;
}